import type { Cart, CartItemBase, Combo, ShippingModelStep } from 'core/domains';

import { useCommand } from 'core/use-cases/utilities';

import { actions } from '../actions';

/**
 * Exports Cart dispatch commands.
 *
 */
export function useCartCommand() {
  const { dispatchCommand } = useCommand();

  /**
   * Clean up page from store to avoid memory leaks
   *
   */
  function cleanCart() {
    dispatchCommand(actions.cartCleanUp());
  }

  /**
   * Add an item to cart
   *
   */
  function addItem(
    productCode: CartItemBase['productCode'],

    itemListName?: string,
    quantity?: number,
  ) {
    dispatchCommand(
      actions.itemAddFetch.request({ productCode, quantity: quantity || 1, itemListName }),
    );
  }

  /**
   * Remove an item from cart
   *
   */
  function deleteItem(
    productCode: CartItemBase['productCode'],
    productPosition: CartItemBase['productPosition'],
    productOrigin: CartItemBase['productOrigin'],
  ) {
    dispatchCommand(
      actions.itemDeleteFetch.request({ productCode, productPosition, productOrigin, quantity: 0 }),
    );
  }

  /**
   * Commando for redirect of checkout flow
   *
   */
  function redirectToCheckout() {
    dispatchCommand(actions.checkoutRedirectFlow());
  }

  /**
   * Toggle option modal for removing item from cart
   *
   */
  function toggleDeleteItem(productCode: CartItemBase['productCode']) {
    dispatchCommand(actions.toggleDeleteConfirmation(productCode));
  }

  /**
   * Toggle option modal for min order confirmation
   *
   */
  function toggleMinOrderConfirmation() {
    dispatchCommand(actions.toggleMinOrderConfirmation());
  }

  /**
   * Toggle option modal
   *
   */
  function toggleComboEditConfirmation(comboCode?: Combo['data']['comboCode']) {
    dispatchCommand(actions.toggleComboEditConfirmation(comboCode));
  }

  /**
   * Update the quantity of item in the cart
   *
   */
  function updateItem(queryOptions: CartItemBase) {
    dispatchCommand(actions.itemUpdateFetch.request(queryOptions));
  }

  /**
   * Set the shipping model
   *
   */
  function setShippingModel(queryOptions: Cart['current']['shippingModel']) {
    dispatchCommand(actions.cartSetShippingModelFetch.request(queryOptions));
  }

  /**
   * Set shipping model shippingModelType
   *
   */
  function setBusinessModel({
    shippingModelType,
    distributionCenterDefaultCode,
  }: {
    shippingModelType: string;
    distributionCenterDefaultCode?: number;
  }) {
    dispatchCommand(actions.setBusinessModel({ shippingModelType, distributionCenterDefaultCode }));
  }

  /**
   * Set Coupon Code
   *
   */
  function setCoupon(coupon: string) {
    dispatchCommand(actions.setCouponFetch.request({ couponNumber: coupon }));
  }

  /**
   * Clear Coupon Code
   *
   */
  function clearCoupon() {
    dispatchCommand(actions.clearCouponFetch.request({ couponNumber: null }));
  }

  function setIsValidCoupon(validCoupon: boolean | null) {
    dispatchCommand(actions.setIsValidCoupon(validCoupon));
  }

  /**
   * Set shipping model distributionCenterCode
   *
   */
  function setDistributionCenter(payload: {
    code: Cart['current']['shippingModel']['distributionCenterCode'];
    address: Cart['current']['distributionCenterAddress'];
  }) {
    dispatchCommand(actions.setDistributionCenter(payload));
  }

  /**
   * Set step of shipping model
   *
   */
  function setShippingModelStep({
    step,
    defaultShippingModelCode,
    defaultDistributionCenterCode,
  }: ShippingModelStep) {
    dispatchCommand(
      actions.setShippingModelStep({
        step,
        defaultShippingModelCode,
        defaultDistributionCenterCode,
      }),
    );
  }

  return {
    addItem,
    cleanCart,
    clearCoupon,
    deleteItem,
    redirectToCheckout,
    setBusinessModel,
    setCoupon,
    setDistributionCenter,
    setIsValidCoupon,
    setShippingModel,
    setShippingModelStep,
    toggleComboEditConfirmation,
    toggleDeleteItem,
    toggleMinOrderConfirmation,
    updateItem,
  };
}
